import {
  Activity,
  ChevronDown,
  Download,
  FileJson,
  Filter,
  FolderOpen,
  GitCompareArrows,
  Grid3X3,
  Info,
  Keyboard,
  Loader2,
  Menu,
  Moon,
  PanelRight,
  Play,
  Radar,
  RefreshCw,
  Save,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  Wand2,
  Eye,
  FileArchive,
  FileUp,
  X,
  XCircle,
  Wrench,
  Database,
  Gauge,
  Layers3,
} from "lucide-react";
import { ProoflineStatus } from "@auraone/aura-ide-kit";
import { readinessTone } from "./core";
import { commands } from "./data";
import type { DatasetTab, Density, Episode, ExportTarget, FilterState, SortState, ViewMode } from "./types";
import { useStudioController } from "./controller";
import {
  AppToast,
  Chip,
  DatasetStatus,
  LoadingIndex,
  Metric,
  PanelTitle,
  RoboticsStudioLogo,
  StateCard,
  UpdateCenter,
  appVersion,
} from "./components";
import {
  CommandPalette,
  Onboarding,
  ViewBody,
} from "./views-release";
import { FilterControls, SensorToggles } from "./views-browse";

const primaryViews: Array<{
  id: ViewMode;
  label: string;
  icon: typeof Grid3X3;
}> = [
  { id: "browse", label: "Browse", icon: Grid3X3 },
  { id: "scrub", label: "Review", icon: Play },
  { id: "clusters", label: "Failures", icon: Radar },
  { id: "compare", label: "Compare", icon: GitCompareArrows },
  { id: "qa", label: "Sensor QA", icon: ShieldCheck },
  { id: "export", label: "Export", icon: UploadCloud },
];

const toolViews: Array<{ id: ViewMode; label: string; icon: typeof Grid3X3 }> =
  [
    { id: "probe", label: "VLA probe", icon: Wand2 },
    { id: "settings", label: "Settings", icon: Settings },
  ];


export default function App() {
  const {
    activeDataset, activeSensorId, activeTabId, chooseEpisode,
    closeCommandPalette, closeOnboarding, clusterProgress, clusterRows,
    clusterRunState, commandQuery, cursor, density,
    diagnosticBufferEnabled, diagnosticEntries, directoryInputRef,
    executeCommand, exportTarget, fileInputRef, filteredEpisodes, filters,
    gridOffset, handleLocalSelection, inspectorCloseRef,
    inspectorDrawerLayout, inspectorOpen, inspectorRef, inspectorTriggerRef,
    intakeMessage, intakeState, manifest, mediaDark, mergeCluster,
    mobileDrawerLayout, navigationCloseRef, navigationTriggerRef,
    networkOnline, notice, openCommandPalette, openDatasetButtonRef,
    openLocalDirectoryPicker, openLocalFilePicker, openSampleDataset, playing,
    recordDiagnostic, runClustering, selectedEpisode, setActiveSensorId,
    setActiveTabId, setCommandQuery, setCursor, setDensity,
    setDiagnosticBufferEnabled, setExportTarget, setFilters, setGridOffset,
    setInspectorOpen, setMediaDark, setNotice, setPlaying, setSidebarOpen,
    setSort, setSpeed, setUpdateState, setView, showOnboarding, showPalette,
    sidebarOpen, sidebarRef, sort, speed, splitCluster, summary, tabs,
    updateEpisode, updateFilters, updateState, view, visibleGrid,
  } = useStudioController();
  return (
    <main
      className={`app media-${mediaDark ? "dark" : "light"}`}
      data-pl-theme="light"
      aria-label="Robotics Studio Open review IDE"
    >
      <input
        ref={fileInputRef}
        className="hidden-file-input"
        type="file"
        multiple
        accept=".json,.jsonl,application/json,application/x-ndjson"
        tabIndex={-1}
        aria-label="Select local dataset files"
        onChange={(event) =>
          void handleLocalSelection(event.currentTarget.files)
        }
      />
      <input
        ref={directoryInputRef}
        className="hidden-file-input"
        type="file"
        multiple
        tabIndex={-1}
        aria-label="Select local dataset folder"
        {...({
          webkitdirectory: "",
          directory: "",
        } as React.InputHTMLAttributes<HTMLInputElement>)}
        onChange={(event) =>
          void handleLocalSelection(event.currentTarget.files)
        }
      />
      <header className="topbar">
        <button
          ref={navigationTriggerRef}
          className="icon-button navigation-toggle"
          onClick={() => setSidebarOpen(true)}
          title="Open navigation"
          aria-label="Open navigation"
          aria-expanded={sidebarOpen}
          aria-controls="workspace-navigation"
        >
          <Menu size={18} />
        </button>
        <div className="brand" role="group" aria-label="Application identity">
          <RoboticsStudioLogo />
          <div className="brand-status">
            <span className="brand-path">
              <DatasetStatus status={activeDataset.status} compact />
              <strong>{activeDataset.name}</strong>
              <em>{activeDataset.indexed.toLocaleString()} ep</em>
              <span className="format-chip">{activeDataset.format}</span>
            </span>
          </div>
        </div>
        <div className="top-actions">
          <button
            className="search-trigger"
            onClick={openCommandPalette}
            title="Open command palette"
            aria-label="Search commands"
          >
            <Search size={15} />
            <span>Search commands</span>
            <kbd>⌘K</kbd>
          </button>
          <button
            className="icon-button ghost"
            onClick={() => setMediaDark((value) => !value)}
            title="Toggle media inspection contrast"
            aria-label={`Use ${mediaDark ? "light" : "dark"} media inspection surface`}
          >
            {mediaDark ? <Moon size={16} /> : <Eye size={16} />}
          </button>
          <button
            className="icon-button ghost"
            title="Settings"
            aria-label="Settings"
            onClick={() => setView("settings")}
          >
            <Settings size={16} />
          </button>
          <button
            className="primary"
            aria-label="Export evidence"
            onClick={() => {
              setExportTarget("Local disk");
              setView("export");
            }}
          >
            <FileArchive size={15} />
            Export evidence
          </button>
          <button
            ref={inspectorTriggerRef}
            className="icon-button inspector-toggle"
            onClick={() => setInspectorOpen(true)}
            title="Open inspector"
            aria-label="Open inspector"
            aria-expanded={inspectorOpen}
            aria-controls="episode-inspector"
          >
            <PanelRight size={18} />
          </button>
        </div>
      </header>
      {activeDataset.provenance.kind === "repository-synthetic-fixture" ? (
        <div className="sample-data-banner" role="status">
          <Info size={14} aria-hidden="true" />
          <span>
            <strong>Sample data.</strong> The hosted build opens a bundled
            synthetic SO-101 fixture ({activeDataset.provenance.recordCount}{" "}
            records). Open local files or a folder to review your own dataset.
          </span>
        </div>
      ) : null}

      <section
        className={`layout ${sidebarOpen ? "sidebar-open" : ""} ${inspectorOpen ? "inspector-open" : "inspector-collapsed"}`}
      >
        {sidebarOpen || inspectorOpen ? (
          <button
            className="drawer-scrim"
            onClick={() => {
              setSidebarOpen(false);
              setInspectorOpen(false);
            }}
            aria-label="Close open drawer"
          />
        ) : null}
        <aside
          ref={sidebarRef}
          id="workspace-navigation"
          className="sidebar"
          aria-label="Datasets and saved views"
          aria-hidden={mobileDrawerLayout && !sidebarOpen ? "true" : undefined}
          tabIndex={0}
        >
          <div className="drawer-heading">
            <strong>Workspace</strong>
            <button
              ref={navigationCloseRef}
              className="icon-button drawer-close"
              onClick={() => {
                setSidebarOpen(false);
                navigationTriggerRef.current?.focus();
              }}
              aria-label="Close navigation"
            >
              <X size={18} />
            </button>
          </div>
          <div className="open-input-row">
            <button
              ref={openDatasetButtonRef}
              className="open-button"
              aria-label="Open JSON dataset"
              onClick={openLocalFilePicker}
            >
              <FileUp size={15} />
              <span>Open dataset</span>
            </button>
            <button
              className="icon-button folder-button"
              onClick={openLocalDirectoryPicker}
              aria-label="Open dataset folder"
              title="Choose a local dataset folder"
            >
              <FolderOpen size={16} />
            </button>
          </div>
          <div className={`intake-status ${intakeState}`} role="status">
            <span>{intakeMessage}</span>
            {intakeState === "error" ? (
              <button onClick={openLocalFilePicker}>Try again</button>
            ) : null}
          </div>
          <PanelTitle icon={Activity} label="Review workflow" />
          <nav className="mode-list" aria-label="Primary review modes">
            {primaryViews.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  aria-label={item.label}
                  className={view === item.id ? "active" : ""}
                  aria-current={view === item.id ? "page" : undefined}
                  onClick={() => {
                    setView(item.id);
                    setSidebarOpen(false);
                  }}
                >
                  <Icon size={17} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
          <PanelTitle icon={Wrench} label="Tools" />
          <nav className="mode-list" aria-label="Robotics Studio tools">
            {toolViews.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  aria-label={item.label}
                  className={view === item.id ? "active" : ""}
                  aria-current={view === item.id ? "page" : undefined}
                  onClick={() => {
                    setView(item.id);
                    setSidebarOpen(false);
                  }}
                >
                  <Icon size={17} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
          <PanelTitle icon={Database} label="Datasets" />
          <div className="tabs-list" role="tablist" aria-label="Open datasets">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                role="tab"
                aria-label={`Dataset ${tab.name}, ${tab.format}, ${tab.indexed} episode records`}
                aria-selected={tab.id === activeTabId}
                className={tab.id === activeTabId ? "tab active" : "tab"}
                onClick={() => setActiveTabId(tab.id)}
              >
                <DatasetStatus status={tab.status} compact />
                <span className="tab-meta">
                  <strong>{tab.name}</strong>
                  <small>
                    {tab.format} · {tab.indexed.toLocaleString()}
                  </small>
                </span>
                <kbd>{index + 1}</kbd>
              </button>
            ))}
          </div>
          <PanelTitle icon={Filter} label="Filters" />
          <FilterControls
            filters={filters}
            sort={sort}
            setFilters={updateFilters}
            setSort={setSort}
          />
          <PanelTitle icon={Save} label="Saved views" />
          {activeDataset.savedViews.length ? (
            activeDataset.savedViews.map((saved) => (
              <button
                key={saved.id}
                className="saved-view"
                onClick={() => {
                  setFilters(saved.filters);
                  setSort(saved.sort);
                }}
              >
                {saved.name}
              </button>
            ))
          ) : (
            <p className="muted small">
              No saved views yet. ⌘⇧V stores the current filter, sort, and
              columns.
            </p>
          )}
        </aside>

        <section className="workarea">
          <label className="mobile-view-switch">
            <span>Workspace view</span>
            <select
              value={view}
              onChange={(event) => setView(event.target.value as ViewMode)}
            >
              {[...primaryViews, ...toolViews].map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          {activeDataset.status === "error" ? (
            <StateCard
              icon={XCircle}
              title="Could not read dataset metadata"
              text={
                activeDataset.error ?? "Expected meta/info.json, got null fps."
              }
            />
          ) : activeDataset.status === "loading" && view === "browse" ? (
            <LoadingIndex dataset={activeDataset} />
          ) : activeDataset.episodes.length === 0 ? (
            <StateCard
              icon={Database}
              title="No episode evidence"
              text="Open the bundled repository fixture or import a JSON manifest or JSONL episode metadata file."
            />
          ) : (
            <ViewBody
              view={view}
              density={density}
              setDensity={setDensity}
              dataset={activeDataset}
              selectedEpisode={selectedEpisode}
              filteredEpisodes={filteredEpisodes}
              visibleGrid={visibleGrid}
              gridOffset={gridOffset}
              setGridOffset={setGridOffset}
              cursor={cursor}
              setCursor={setCursor}
              playing={playing}
              setPlaying={setPlaying}
              speed={speed}
              setSpeed={setSpeed}
              chooseEpisode={chooseEpisode}
              updateEpisode={updateEpisode}
              exportTarget={exportTarget}
              setExportTarget={setExportTarget}
              manifest={manifest}
              diagnosticBufferEnabled={diagnosticBufferEnabled}
              setDiagnosticBufferEnabled={setDiagnosticBufferEnabled}
              diagnosticEntries={diagnosticEntries}
              mediaDark={mediaDark}
              setMediaDark={setMediaDark}
              networkOnline={networkOnline}
              updateState={updateState}
              setUpdateState={setUpdateState}
              activeSensorId={activeSensorId}
              setActiveSensorId={setActiveSensorId}
              clusterRows={clusterRows}
              clusterRunState={clusterRunState}
              clusterProgress={clusterProgress}
              runClustering={runClustering}
              splitCluster={splitCluster}
              mergeCluster={mergeCluster}
              recordDiagnostic={recordDiagnostic}
            />
          )}
        </section>

        <aside
          ref={inspectorRef}
          id="episode-inspector"
          className="right-rail"
          aria-label="Episode inspector"
          aria-hidden={
            inspectorDrawerLayout && !inspectorOpen ? "true" : undefined
          }
          tabIndex={0}
        >
          <div className="drawer-heading">
            <strong>Inspector</strong>
            <button
              ref={inspectorCloseRef}
              className="icon-button drawer-close"
              onClick={() => {
                setInspectorOpen(false);
                inspectorTriggerRef.current?.focus();
              }}
              aria-label="Close inspector"
            >
              <X size={18} />
            </button>
          </div>
          <PanelTitle icon={Info} label="Selected episode" />
          <dl className="inspector-record">
            <div>
              <dt>Episode</dt>
              <dd>{selectedEpisode.id}</dd>
            </div>
            <div>
              <dt>Task</dt>
              <dd>
                {selectedEpisode.task
                  ? selectedEpisode.task.replaceAll("_", " ")
                  : "Unknown"}
              </dd>
            </div>
            <div>
              <dt>Decision</dt>
              <dd>
                <ProoflineStatus
                  tone={
                    selectedEpisode.reviewed === "reviewed"
                      ? "success"
                      : selectedEpisode.reviewed === "unreviewed"
                        ? "review"
                        : "warning"
                  }
                  label={
                    selectedEpisode.reviewed === "reviewed"
                      ? "Reviewed"
                      : selectedEpisode.reviewed === "unreviewed"
                        ? "Needs review"
                        : "Unknown"
                  }
                />
              </dd>
            </div>
            <div>
              <dt>Outcome</dt>
              <dd>
                <ProoflineStatus
                  tone={
                    selectedEpisode.success === "success"
                      ? "success"
                      : selectedEpisode.success === "failure"
                        ? "danger"
                        : "warning"
                  }
                  label={selectedEpisode.success}
                />
              </dd>
            </div>
          </dl>
          <PanelTitle icon={Gauge} label="Dataset health" />
          <div className="health-grid">
            <Metric
              label="Visible"
              value={`${filteredEpisodes.length} / ${activeDataset.indexed.toLocaleString()}`}
            />
            <Metric
              label="Avg readiness"
              value={
                summary.avgReadiness === null
                  ? "Unknown"
                  : `${summary.avgReadiness}`
              }
              tone={readinessTone(summary.avgReadiness)}
            />
            <Metric
              label="Failures"
              value={summary.failures.toString()}
              tone={summary.failures ? "warn" : "pass"}
            />
            <Metric
              label="QA flags"
              value={
                summary.qaUnknown
                  ? `${summary.qaFailures} + ${summary.qaUnknown} unknown`
                  : summary.qaFailures.toString()
              }
              tone={
                summary.qaFailures
                  ? "warn"
                  : summary.qaUnknown
                    ? "neutral"
                    : "pass"
              }
            />
          </div>
          <PanelTitle icon={Layers3} label="Sensor streams" />
          <SensorToggles
            episode={selectedEpisode}
            updateEpisode={updateEpisode}
          />
          <PanelTitle icon={Keyboard} label="Shortcuts" />
          <ul className="shortcut-list">
            {commands.slice(0, 7).map((command) => (
              <li key={command.id}>
                <span>{command.title}</span>
                <kbd>{command.key}</kbd>
              </li>
            ))}
          </ul>
        </aside>
      </section>

      <footer className="statusbar">
        <ProoflineStatus tone="info" label="Source-build fixture renderer" />
        <ProoflineStatus tone="info" label="Local-only session" />
        <ProoflineStatus
          tone={networkOnline ? "info" : "blocked"}
          label={
            networkOnline
              ? "Browser online; destinations unverified"
              : "Browser offline"
          }
        />
        <span>
          {filteredEpisodes.length.toLocaleString()} visible /{" "}
          {activeDataset.indexed.toLocaleString()} episodes
        </span>
        <span>Saved {activeDataset.lastSaved}</span>
        <span className="status-spacer" />
        <span>v{appVersion}</span>
        <span>
          <kbd>⇧⌘E</kbd> Export
        </span>
      </footer>

      {notice ? (
        <AppToast notice={notice} close={() => setNotice(null)} />
      ) : null}
      {showOnboarding ? (
        <Onboarding
          close={closeOnboarding}
          openSample={openSampleDataset}
          openLocal={openLocalFilePicker}
        />
      ) : null}
      {showPalette ? (
        <CommandPalette
          query={commandQuery}
          setQuery={setCommandQuery}
          close={closeCommandPalette}
          executeCommand={executeCommand}
        />
      ) : null}
    </main>
  );
}

